import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { generateKey } from 'utilities/index';

import { GREEK } from 'staticAssets/greekCharacters';

import {
  getTableData,
  getCategoricalRowDataSet,
  getLevelsRowDataSet,
  getPValue,
  getDF,
  getChiSquare,
  getN
} from 'store/scenes/inferentialStatistics/selectors';

const cellFormats = ['Observed Count', 'Expected Count'];

const ChiSquareTestForIndependence = ({ isTestForHomogeneity }) => {
  const tableData = useSelector(state => getTableData(state));
  const pValue = useSelector(state => getPValue(state));
  const df = useSelector(state => getDF(state));
  const chiSquare = useSelector(state => getChiSquare(state));
  const n = useSelector(state => getN(state));
  const observedCounts = useSelector(state => getCategoricalRowDataSet(state));
  const level = useSelector(state => getLevelsRowDataSet(state));

  const tableHeaders = [observedCounts, 'Cell Format', ...level, 'Total'];

  const renderHeadingForTestForIndependence = () => (
    <div data-testId="headingForIndependenceTest" >
      <h3>Chi-Square Test for Independence</h3>
      <div>
        <span className="font-900 font-16 mr-5">H<sub>0</sub>:</span>
        <span>The two variables are independent (not associated)</span>
      </div>
      <div>
        <span className="font-900 font-16 mr-5">H<sub>A</sub>:</span>
        <span>The two variables are not independent (are associated)</span>
      </div>
    </div>
  );

  const renderHeadingForTestForHomogeneity = () => (
    <div data-testId="headingForHomogeneityTest">
      <h3>Chi-Square Test for Homogeneity</h3>
      <div>
        <span className="font-900 font-16 mr-5">H<sub>0</sub>:</span>
        <span>Category proportions are the same for all groups</span>
      </div>
      <div>
        <span className="font-900 font-16 mr-5">H<sub>A</sub>:</span>
        <span>Category proportions are not all the same for all groups</span>
      </div>
    </div>
  );

  return (
    <div className="content-item">
      {
        isTestForHomogeneity ? renderHeadingForTestForHomogeneity() : renderHeadingForTestForIndependence()
      }
      <table className="infStatMarkupChi-table mb-10 mt-10">
        <tbody>
          <tr>
            <td>n:</td>
            <td>{n}</td>
          </tr>
          <tr>
            <td>df:</td>
            <td>{df}</td>
          </tr>
          <tr>
            <td>{GREEK.CHI}<sup>2</sup> Statistic:</td>
            <td>{chiSquare}</td>
          </tr>
          <tr>
            <td>P Value:</td>
            <td>{pValue}</td>
          </tr>

        </tbody>
      </table>
      <table className="infStat-irregularTable">
        <thead>
          <tr>
            {tableHeaders.map(columnHeader => <th key={generateKey(columnHeader)}>{columnHeader}</th>)}
          </tr>
        </thead>

        {tableData.levelNames.map((levelName, i) => (
          <tbody key={levelName}>
            <tr>
              <th rowSpan="3" scope="rowgroup">{levelName}</th>
              <td>{cellFormats[0]}</td>
              {tableData.levelCounts[i].map(levelCountValue => <td key={generateKey(levelCountValue)}>{levelCountValue}</td>)}
              <td rowSpan="3">{tableData.observedCount[i]}</td>
            </tr>
            <tr>
              <td>{cellFormats[1]}</td>
              {tableData.expectedFrequencies[i].map(expectedFrequency => <td key={generateKey(expectedFrequency)}>{expectedFrequency}</td>)}
            </tr>
            <tr>
              <td>Contribution to {GREEK.CHI}<sup>2</sup></td>
              {tableData.contributionsToChiSquare[i].map(contributionToChiSquare => <td key={generateKey(contributionToChiSquare)}>{contributionToChiSquare}</td>)}
            </tr>
          </tbody>
        ))}
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            <td>Observed Count</td>
            {tableData.observedCountColumnsValues.map(columnTotal => <td key={generateKey(columnTotal)}>{columnTotal}</td>)}
            <td>{n}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

ChiSquareTestForIndependence.propTypes = {
  isTestForHomogeneity: PropTypes.bool.isRequired
};

export default ChiSquareTestForIndependence;
